import { supabase } from '@/lib/supabase'
import type { MonitorItem } from '../types-monitor'
import { fetchAssetsMeta } from '../utils/assets'
import { getAllowedSymbolsForCurrentUser } from '@/modules/plans/services/planAssetPolicyService'

const KNOWN_CONFLUENCES = ['rsi', 'ema', 'volume', 'fibo']

export async function listMonitoresAtivos(): Promise<MonitorItem[]> {
  let allowed: string[] | null = null
  try {
    allowed = await getAllowedSymbolsForCurrentUser()
  } catch {
    allowed = null
  }

  const normAllowed = allowed && allowed.length
    ? Array.from(new Set(allowed.map((s) => String(s || '').trim().toUpperCase()).filter(Boolean)))
    : null

  let query = supabase
    .from('active_monitoring')
    .select('id,symbol,status,trend_direction,confluence_factors,updated_at,menor_timeframe,maior_timeframe')
    .eq('status', 'active')
    .order('updated_at', { ascending: false })
    .limit(200)

  if (normAllowed && normAllowed.length > 0) {
    query = query.in('symbol', normAllowed)
  }

  const { data: raws, error } = await query
  if (error) throw error
  const rows = Array.isArray(raws) ? raws : []

  const symbols = Array.from(new Set(rows.map((r: any) => String(r.symbol || '').trim()).filter(Boolean)))
  const assets = await fetchAssetsMeta(symbols)
  const bySymbol = new Map(assets.map((a) => [String(a.symbol).toUpperCase(), a]))

  const totalPossible = (row: any): number => {
    try {
      const set = new Set<string>(KNOWN_CONFLUENCES)
      const cf = row?.confluence_factors
      if (cf && typeof cf === 'object') {
        Object.keys(cf).forEach((k) => {
          const key = k.toLowerCase()
          if (key.includes('ema')) set.add('ema')
          else if (key.includes('rsi')) set.add('rsi')
          else if (key.includes('vol')) set.add('volume')
          else if (key.includes('fibo') || key.includes('fib')) set.add('fibo')
          else set.add(key.split('_')[0])
        })
      }
      return Math.max(4, set.size)
    } catch { return 4 }
  }

  function toObjective(trend?: string | null): 'Long' | 'Short' | '' {
    const t = String(trend || '').toLowerCase()
    if (t === 'bullish' || t === 'alta' || t === 'long') return 'Long'
    if (t === 'bearish' || t === 'baixa' || t === 'short') return 'Short'
    return ''
  }
  function tfShort(tf?: string | null): string | null {
    const t = String(tf || '').toUpperCase()
    if (!t) return null
    if (t === '5') return '5min'
    if (t === '15') return '15min'
    if (t === '30') return '30min'
    if (t === '60') return '1h'
    if (t === '240') return '4h'
    if (t === '720') return '12h'
    if (t === 'D') return '1D'
    if (t === 'W') return '1W'
    if (t === 'M') return '1M'
    return t
  }

  return rows.map((r: any) => {
    const sym = String(r.symbol || '').toUpperCase()
    const meta = bySymbol.get(sym)
    const strength = Number(r?.confluence_factors?.confluence_strength || r?.confluence_strength || 0)
    const denom = totalPossible(r)
    const pct = Math.max(0, Math.min(100, Math.round((strength / (denom || 4)) * 100)))
    const item: MonitorItem = {
      id: String(r.id || crypto.randomUUID()),
      symbol: sym,
      name: (meta as any)?.name || (meta as any)?.base_asset || null,
      logo_url: (meta as any)?.logo_url || null,
      trend: (String(r.trend_direction || '').toLowerCase() as any) || null,
      objective: toObjective(r?.trend_direction),
      confluencePercent: pct,
      status: r?.status || null,
      fractalLower: tfShort(r?.menor_timeframe) || null,
      fractalUpper: tfShort(r?.maior_timeframe) || null,
    }
    return item
  })
}

export async function getMonitorById(id: string): Promise<any | null> {
  const { data, error } = await supabase
    .from('active_monitoring')
    .select('*')
    .eq('id', id)
    .maybeSingle()
  if (error) throw error
  return data as any
}

